import { CademyError } from '@shared/domain-shared';
import { Field, Form } from '@shared/types';
import { QueryFunction, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export type EducatorForm = Form & {
    fields: Array<Field>;
};

const getEducatorForm = async ({ formId }: { formId: string }): Promise<EducatorForm> => {
    const response = await fetch(`${process.env.NEXT_PUBLIC_CADEMY_API_URL}/forms/${formId}/view`);

    if (!response.ok) {
        if (response.headers.get('content-type') === 'application/problem+json') {
            const problemDetails = await response.json();
            const error = CademyError.fromObject(problemDetails);
            throw error;
        }
        const error = await response.text();
        throw new Error(error);
    }

    return await response.json();
};

type QueryKey = [queryType: 'educator-form', formId: string];
const educatorFormQueryFunction: QueryFunction<EducatorForm, QueryKey> = ({ queryKey }) => {
    const formId = queryKey[1];
    return getEducatorForm({ formId });
};

export type UseEducatorFormOptions = Omit<
    UseQueryOptions<EducatorForm, unknown, EducatorForm, QueryKey>,
    'queryKey' | 'queryFn'
>;
export const useEducatorForm = (formId: string, options?: UseEducatorFormOptions) => {
    const queryKey = useMemo((): QueryKey => {
        return ['educator-form', formId];
    }, [formId]);
    return useQuery({
        queryKey,
        queryFn: educatorFormQueryFunction,
        ...options,
    });
};
